/**
 * PWAInstallBanner
 *
 * Показывает баннер установки PWA при первом открытии в браузере.
 * После установки автоматически запрашивает необходимые разрешения:
 * уведомления и геолокацию (опционально).
 *
 * Логика показа:
 *  - Скрыт, если PWA уже установлено (standalone mode)
 *  - Показывается сразу при наличии beforeinstallprompt (Chrome/Edge/Android)
 *  - На iOS Safari показывает ручную инструкцию через Share → Add to Home Screen
 *  - Не мешает основному контенту (fixed bottom sheet)
 *  - Исчезает после установки, затем (если разрешений нет) предлагает их выдать
 */

import { useCallback, useEffect, useState } from 'react';
import { usePWAInstall }                    from './usePWAInstall';
import { C, F }                             from '../design-system/tokens';

// ─── Типы состояний баннера ─────────────────────────────────────────────────
type BannerState =
  | 'install'         // Предложение установить PWA
  | 'installing'      // Идёт установка
  | 'permissions'     // Запрос разрешений после установки
  | 'done';           // Всё готово, баннер скрыт

// ─── Хелперы разрешений ─────────────────────────────────────────────────────
async function requestNotifications(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied')  return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

// ─── Компонент ────────────────────────────────────────────────────────────────
export function PWAInstallBanner() {
  const { canInstall, isInstalled, isIOS, install, dismiss, isDismissed } = usePWAInstall();

  const [state, setState]                 = useState<BannerState>('install');
  const [notifGranted, setNotifGranted]   = useState(
    () => 'Notification' in window && Notification.permission === 'granted',
  );
  const [visible, setVisible]             = useState(false);
  const [iosHintOpen, setIosHintOpen]     = useState(false);

  // Показываем баннер с небольшой задержкой, чтобы не мешать первой загрузке
  useEffect(() => {
    if (isInstalled) return;
    if (isDismissed && !isIOS) return;

    const t = window.setTimeout(() => {
      if (canInstall || isIOS) setVisible(true);
    }, 1800);

    return () => window.clearTimeout(t);
  }, [canInstall, isIOS, isInstalled, isDismissed]);

  // После установки переходим к экрану разрешений
  useEffect(() => {
    if (isInstalled && state === 'installing') {
      setState('permissions');
      setVisible(true);
    }
  }, [isInstalled, state]);

  // Обработчик кнопки «Установить»
  const handleInstall = useCallback(async () => {
    if (isIOS) {
      setIosHintOpen(true);
      return;
    }
    setState('installing');
    const outcome = await install();
    if (outcome === 'dismissed') {
      setState('install');
    }
  }, [isIOS, install]);

  // Запрос уведомлений
  const handleRequestNotifications = useCallback(async () => {
    const granted = await requestNotifications();
    setNotifGranted(granted);
  }, []);

  // Завершение
  const handleDone = useCallback(() => {
    setVisible(false);
    setState('done');
  }, []);

  const handleDismiss = useCallback(() => {
    dismiss();
    setVisible(false);
  }, [dismiss]);

  // ── Скрыт ─────────────────────────────────────────────────────────────────
  if (!visible || state === 'done') return null;

  return (
    <>
      {/* Backdrop для iOS hint */}
      {iosHintOpen && (
        <div
          onClick={() => setIosHintOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,.6)',
            zIndex: 9998,
            backdropFilter: 'blur(4px)',
          }}
        />
      )}

      {/* iOS Share hint */}
      {iosHintOpen && (
        <div style={styles.iosHint}>
          <div style={styles.iosHintArrow} />
          <p style={styles.iosHintTitle}>Как установить Кворум</p>
          <ol style={styles.iosHintList}>
            <li>Нажмите <ShareIcon /> <strong>Поделиться</strong> в Safari</li>
            <li>Выберите <strong>На экран «Домой»</strong></li>
            <li>Нажмите <strong>Добавить</strong></li>
          </ol>
          <button style={styles.iosHintClose} onClick={() => setIosHintOpen(false)}>
            Понятно
          </button>
        </div>
      )}

      {/* Основной баннер */}
      <div style={styles.backdrop} onClick={state === 'install' ? handleDismiss : undefined} />
      <div style={styles.sheet}>
        <div style={styles.sheetInner}>

          {/* ── Экран установки ───────────────────────────────────────────── */}
          {(state === 'install' || state === 'installing') && (
            <div style={styles.inner}>
              <div style={styles.iconWrap}>
                <AppLogoIcon />
              </div>
              <div style={styles.textBlock}>
                <p style={styles.title}>Установить Кворум</p>
                <p style={styles.sub}>
                  Работайте офлайн, получайте уведомления о заявках и открывайте без браузера
                </p>
              </div>
              <div style={styles.actions}>
                <button
                  style={{ ...styles.btn, ...styles.btnPrimary }}
                  onClick={handleInstall}
                  disabled={state === 'installing'}
                >
                  {state === 'installing' ? (
                    <span style={styles.spinner} />
                  ) : isIOS ? (
                    'Добавить на экран'
                  ) : (
                    'Установить'
                  )}
                </button>
                <button style={{ ...styles.btn, ...styles.btnGhost }} onClick={handleDismiss}>
                  Не сейчас
                </button>
              </div>
            </div>
          )}

          {/* ── Экран разрешений ──────────────────────────────────────────── */}
          {state === 'permissions' && (
            <div style={styles.inner}>
              <div style={{ ...styles.iconWrap, background: C.grS }}>
                <CheckIcon />
              </div>
              <div style={styles.textBlock}>
                <p style={styles.title}>Кворум установлен!</p>
                <p style={styles.sub}>
                  Разрешите уведомления — и вы будете получать новые заявки мгновенно
                </p>
              </div>

              <div style={styles.permList}>
                <PermRow
                  icon={<BellIcon />}
                  label="Push-уведомления"
                  desc="Новые заявки и изменения статусов"
                  granted={notifGranted}
                  onRequest={handleRequestNotifications}
                />
              </div>

              <button
                style={{ ...styles.btn, ...styles.btnPrimary, marginTop: 8 }}
                onClick={handleDone}
              >
                {notifGranted ? 'Готово' : 'Пропустить'}
              </button>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

// ─── Вспомогательные компоненты ──────────────────────────────────────────────
function PermRow({
  icon, label, desc, granted, onRequest,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
  granted: boolean;
  onRequest: () => void;
}) {
  return (
    <div style={styles.permRow}>
      <div style={styles.permIcon}>{icon}</div>
      <div style={styles.permText}>
        <span style={styles.permLabel}>{label}</span>
        <span style={styles.permDesc}>{desc}</span>
      </div>
      {granted ? (
        <span style={styles.permGranted}>✓</span>
      ) : (
        <button style={styles.permBtn} onClick={onRequest}>
          Разрешить
        </button>
      )}
    </div>
  );
}

// ─── SVG-иконки ─────────────────────────────────────────────────────────────
function AppLogoIcon() {
  return (
    <img
      src="/icon-192.png"
      alt="Логотип Кворум"
      width={36}
      height={36}
      style={{ display: 'block', width: 36, height: 36, borderRadius: 10 }}
    />
  );
}
function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.gr} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginInline: 2 }}>
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// ─── Стили (inline, без зависимостей) ────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(7,9,15,.55)',
    zIndex: 9990,
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
  },
  sheet: {
    position:     'fixed',
    bottom:       0,
    left:         0,
    right:        0,
    background:   C.card,
    borderTop:    `1px solid ${C.b}`,
    borderRadius: '20px 20px 0 0',
    zIndex:       9991,
    boxShadow:    `0 -8px 40px rgba(0,0,0,.5)`,
    animation:    'sb .28s cubic-bezier(.22,1,.36,1) both',
  },
  sheetInner: {
    maxWidth:   480,
    margin:     '0 auto',
  },
  inner: {
    padding:        '28px 24px 32px',
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            16,
  },
  iconWrap: {
    width:          64,
    height:         64,
    borderRadius:   18,
    background:     `rgba(255,95,31,.1)`,
    border:         `1px solid rgba(255,95,31,.2)`,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  textBlock: {
    textAlign: 'center',
    gap:       6,
    display:   'flex',
    flexDirection: 'column',
  },
  title: {
    fontFamily: F.head,
    fontSize:   22,
    fontWeight: 700,
    color:      C.txt,
    letterSpacing: '.3px',
  },
  sub: {
    fontFamily: F.body,
    fontSize:   14,
    color:      C.sub,
    lineHeight: 1.5,
    maxWidth:   280,
    margin:     '0 auto',
  },
  actions: {
    display:       'flex',
    flexDirection: 'column',
    gap:           8,
    width:         '100%',
  },
  btn: {
    width:         '100%',
    padding:       '14px 20px',
    borderRadius:  12,
    border:        'none',
    fontFamily:    F.body,
    fontSize:      15,
    fontWeight:    600,
    cursor:        'pointer',
    display:       'flex',
    alignItems:    'center',
    justifyContent:'center',
    gap:            8,
    transition:    'opacity .12s, transform .1s',
  },
  btnPrimary: {
    background: C.acc,
    color:      '#fff',
  },
  btnGhost: {
    background: C.surf,
    color:      C.sub,
    border:     `1px solid ${C.b}`,
  },
  spinner: {
    width:        18,
    height:       18,
    borderRadius: '50%',
    border:       '2px solid rgba(255,255,255,.3)',
    borderTopColor: '#fff',
    animation:    'sp .7s linear infinite',
    display:      'inline-block',
  },
  permList: {
    width:   '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  permRow: {
    display:       'flex',
    alignItems:    'center',
    gap:           12,
    padding:       '12px 14px',
    background:    C.surf,
    border:        `1px solid ${C.b}`,
    borderRadius:  12,
  },
  permIcon: {
    color:          C.sub,
    flexShrink:     0,
    display:        'flex',
    alignItems:     'center',
  },
  permText: {
    flex:          1,
    display:       'flex',
    flexDirection: 'column',
    gap:            2,
  },
  permLabel: {
    fontFamily: F.body,
    fontSize:   14,
    fontWeight: 600,
    color:      C.txt,
  },
  permDesc: {
    fontFamily: F.body,
    fontSize:   12,
    color:      C.sub,
  },
  permGranted: {
    color:      C.gr,
    fontWeight: 700,
    fontSize:   16,
    flexShrink: 0,
  },
  permBtn: {
    padding:      '6px 12px',
    borderRadius: 8,
    border:       `1px solid ${C.acc}`,
    background:   C.accS,
    color:        C.acc,
    fontFamily:   F.body,
    fontSize:     12,
    fontWeight:   600,
    cursor:       'pointer',
    flexShrink:   0,
    whiteSpace:   'nowrap',
  },
  // iOS hint
  iosHint: {
    position:     'fixed',
    bottom:       80,
    left:         '50%',
    transform:    'translateX(-50%)',
    width:        'calc(100% - 32px)',
    maxWidth:     380,
    background:   C.cardHi,
    border:       `1px solid ${C.b}`,
    borderRadius: 16,
    padding:      '20px 20px 16px',
    zIndex:       9999,
    boxShadow:    '0 8px 32px rgba(0,0,0,.5)',
  },
  iosHintArrow: {
    position:     'absolute',
    bottom:       -8,
    left:         '50%',
    transform:    'translateX(-50%) rotate(45deg)',
    width:        16,
    height:       16,
    background:   C.cardHi,
    border:       `1px solid ${C.b}`,
    borderTop:    'none',
    borderLeft:   'none',
  },
  iosHintTitle: {
    fontFamily: F.head,
    fontSize:   16,
    fontWeight: 700,
    color:      C.txt,
    marginBottom: 12,
  },
  iosHintList: {
    fontFamily:  F.body,
    fontSize:    14,
    color:       C.sub,
    lineHeight:  1.7,
    paddingLeft: 18,
    marginBottom: 16,
  },
  iosHintClose: {
    width:        '100%',
    padding:      '10px',
    borderRadius: 10,
    border:       'none',
    background:   C.acc,
    color:        '#fff',
    fontFamily:   F.body,
    fontSize:     14,
    fontWeight:   600,
    cursor:       'pointer',
  },
};
